import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Plus, Trash2, Edit2, Search, Star, Tag, ToggleLeft, ToggleRight, Image } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const CATEGORIES = ['Electronics', 'Fashion', 'Home', 'Beauty', 'Sports', 'Books', 'Gaming', 'Food', 'Pet', 'General'];

export default function AdminProducts() {
  const { token, API } = useAuth();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', description: '', price: '', original_price: '', image_url: '',
    store: '', store_domain: '', category: 'General', cashback_percent: '10',
    affiliate_url: '', featured: false, tags: ''
  });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search }
      });
      setProducts(res.data.products);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const resetForm = () => {
    setForm({
      name: '', description: '', price: '', original_price: '', image_url: '',
      store: '', store_domain: '', category: 'General', cashback_percent: '10',
      affiliate_url: '', featured: false, tags: ''
    });
    setEditingProduct(null);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name, description: p.description || '', price: String(p.price),
      original_price: p.original_price ? String(p.original_price) : '',
      image_url: p.image_url || '', store: p.store, store_domain: p.store_domain,
      category: p.category, cashback_percent: String(p.cashback_percent),
      affiliate_url: p.affiliate_url, featured: p.featured,
      tags: (p.tags || []).join(', ')
    });
    setEditingProduct(p);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      cashback_percent: parseFloat(form.cashback_percent),
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : []
    };
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      resetForm();
      setShowForm(false);
      fetchProducts();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchProducts();
    } catch (e) { alert('Failed'); }
  };

  const toggleActive = async (product) => {
    try {
      await axios.put(`${API}/admin/products/${product.id}`, { active: !product.active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (e) { alert('Failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="admin-products-search"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-[#121214] border-white/10 text-white pl-10 h-10"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-zinc-500">{total} products</span>
          <button
            data-testid="admin-add-product-btn"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="honey-btn px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Product
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Product</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Store</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Price</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Cashback</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Clicks</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Status</th>
                <th className="text-right text-xs font-medium text-zinc-500 px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={p.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`admin-product-row-${i}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ShoppingBag className="w-4 h-4 text-zinc-700" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{p.name}</p>
                        <p className="text-[10px] text-zinc-600">{p.category} {p.featured && '⭐ Featured'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-300">{p.store}</td>
                  <td className="px-5 py-3 text-sm font-mono text-white">${p.price}</td>
                  <td className="px-5 py-3">
                    <span className="text-sm font-mono text-emerald-400">{p.cashback_percent}%</span>
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-400">{p.click_count || 0}</td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => toggleActive(p)}
                      className={`text-xs px-2 py-0.5 rounded-full ${p.active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}
                    >
                      {p.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(p)} className="p-1.5 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10" data-testid={`admin-edit-product-${i}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteProduct(p.id)} className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`admin-delete-product-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {products.length === 0 && (
          <div className="text-center py-12">
            <ShoppingBag className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No products yet</p>
          </div>
        )}
      </div>

      {/* Add/Edit Product Dialog */}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); resetForm(); }}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Product Name *</Label>
                <Input data-testid="admin-product-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="bg-[#09090B] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Description</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Price ($) *</Label>
                <Input data-testid="admin-product-price" type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Original Price ($)</Label>
                <Input type="number" step="0.01" value={form.original_price} onChange={e => setForm({...form, original_price: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Cashback %</Label>
                <Input data-testid="admin-product-cashback" type="number" step="0.5" value={form.cashback_percent} onChange={e => setForm({...form, cashback_percent: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Store Name *</Label>
                <Input data-testid="admin-product-store" value={form.store} onChange={e => setForm({...form, store: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Store Domain *</Label>
                <Input data-testid="admin-product-domain" value={form.store_domain} onChange={e => setForm({...form, store_domain: e.target.value})} placeholder="amazon.com" className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Affiliate URL *</Label>
              <Input data-testid="admin-product-url" value={form.affiliate_url} onChange={e => setForm({...form, affiliate_url: e.target.value})} placeholder="https://..." className="bg-[#09090B] border-white/10 text-white mt-1" required />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Image URL</Label>
              <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." className="bg-[#09090B] border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Tags (comma separated)</Label>
              <Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="tech, wireless, sale" className="bg-[#09090B] border-white/10 text-white mt-1" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.featured} onCheckedChange={v => setForm({...form, featured: v})} className="data-[state=checked]:bg-amber-500" />
              <Label className="text-xs text-zinc-400">Featured product</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="flex-1 px-4 py-2.5 rounded-lg text-sm bg-white/5 text-zinc-400 hover:bg-white/10">Cancel</button>
              <button type="submit" data-testid="admin-product-submit" disabled={saving} className="flex-1 honey-btn px-4 py-2.5 rounded-lg text-sm font-bold disabled:opacity-50">
                {saving ? 'Saving...' : editingProduct ? 'Update' : 'Create Product'}
              </button>
            </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
